<template>
    <form ref="form" :action="endpoint" method="post">
        <input type="hidden" name="_token" :value="csrf"/>

        <div class="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">Information</h3>
                    <p class="mt-1 text-sm text-gray-500">
                        Here you'll find information about the user and it's permissions
                    </p>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">

                    <div class="grid grid-cols-6 gap-6">
                        <div class="col-span-6 sm:col-span-3">
                            <input-field type="text"
                                         label="Full Name"
                                         name="name"
                                         :required="true"
                                         :errors="errors?.name"
                                         v-model="form.name"/>
                        </div>

                        <div class="col-span-6 sm:col-span-3">
                            <input-field type="email"
                                         label="Email"
                                         name="email"
                                         :required="true"
                                         :errors="errors?.email"
                                         v-model="form.email"/>
                        </div>

                        <template v-if="isAdmin">
                            <div v-if="!portfolioId && !organisationId" class="col-span-6 sm:col-span-3">
                                <v-select-field label="User Type"
                                                name="user_type"
                                                :options="userTypes"
                                                :errors="errors?.user_type"
                                                :transform="o => o.value"
                                                v-model="form.user_type" />
                            </div>

                            <template v-if="!organisationId && isAdmin && form.user_type === 'client'">
                                <div v-if="isPortfolioManagerFeatureEnabled" class="col-span-6 sm:col-span-3">
                                    <div class="flex items-center mt-7">
                                        <input v-model="form.is_manager" value="1" id="is_manager" name="is_manager" type="checkbox"
                                               class="h-4 w-4 text-uknar-secondary focus:ring-uknar-secondary border-gray-300 rounded">
                                        <label for="is_manager" class="ml-2 block text-sm text-gray-900">
                                            Level 5
                                        </label>
                                    </div>
                                </div>

                                <template v-if="form.is_manager">
                                    <div class="col-span-6 sm:col-span-3">
                                        <input-field
                                            type="date"
                                            label="Expiry Date"
                                            name="expiry_date"
                                            :required="false"
                                            :errors="errors?.expiry_date"
                                            v-model="form.expiry_date"/>
                                    </div>

                                    <div v-if="!portfolioId" class="col-span-6 sm:col-span-3">
                                        <v-select-field label="Portfolios"
                                                        name="portfolio_ids[]"
                                                        option-label="name"
                                                        :multiple="true"
                                                        :options="portfolios"
                                                        :errors="errors?.portfolio_ids"
                                                        :transform="o => o.id"
                                                        v-model="form.portfolio_ids" />
                                    </div>
                                </template>
                            </template>
                        </template>

                        <div class="col-span-6 sm:col-span-3">
                            <input-field type="text"
                                         label="Position"
                                         name="position"
                                         :required="false"
                                         :errors="errors?.position"
                                         v-model="form.position"/>
                        </div>

                        <div class="col-span-6 sm:col-span-3">
                            <input-field type="number"
                                         label="Phone Number"
                                         name="number"
                                         :required="false"
                                         :errors="errors?.number"
                                         v-model="form.number"/>
                        </div>

                        <div class="col-span-6 sm:col-span-3">
                            <input-field type="text"
                                         label="Contact Category"
                                         name="contact_category"
                                         :required="false"
                                         :errors="errors?.contact_category"
                                         v-model="form.contact_category"/>
                        </div>

                        <div class="col-span-6 sm:col-span-3">
                            <textarea-field label="Contact Note"
                                            name="contact_note"
                                            :required="false"
                                            :errors="errors?.contact_note"
                                            v-model="form.contact_note"/>
                        </div>
                    </div>
                    <template v-if="form.user_type === 'client'">
                        <div class="grid grid-cols-6 gap-6 mt-2">
                            <template v-if="!form.is_manager">
                                <div class="col-span-6">
                                    <strong>Add roles & permissions for user</strong>
                                </div>
                                <div v-if="!organisationId" class="col-span-6 sm:col-span-3">
                                    <v-select-field label="Choose organisations"
                                                    name="client"
                                                    :options="clients"
                                                    option-label="name"
                                                    required
                                                    multiple
                                                    @input="handleChosenClientChange"
                                                    :errors="errors?.client_ids"
                                                    v-model="chosenClients" />
                                </div>

                                <template v-if="chosenClients.length">
                                    <div class="col-span-6 sm:col-span-3" :key="rolesSelectKey">
                                        <v-select-field label="Role"
                                                        name="role"
                                                        required
                                                        :options="roles"
                                                        option-label="name"
                                                        :transform="o => o.id"
                                                        :errors="errors?.role_id ?? []"
                                                        v-model="form.role_id" />
                                    </div>
                                    <div class="col-span-6 sm:col-span-3">
                                        <input-field type="date"
                                                     label="Expiry Date"
                                                     name="expiry_date"
                                                     :errors="errors?.expiry_date"
                                                     v-model="form.expiry_date" />
                                    </div>

                                    <template v-if="clients">
                                        <div class="col-span-6 font-bold border-b mt-4 mb-3 pb-3">
                                            Select properties <span class="text-red-600 text-xs">*</span>
                                            <p v-if="errors?.properties" class="text-red-500 text-xs italic font-normal">
                                                <div v-for="error of errors.properties">{{ error }}</div>
                                            </p>

                                            <div class="flex items-center mt-3">
                                                <input @change="handleCheckAll($event)" v-model="checkAllProperties" id="check_all" type="checkbox" class="h-4 w-4 text-uknar-secondary focus:ring-uknar-secondary border-gray-300 rounded">
                                                <label for="check_all" class="ml-2 block text-sm text-gray-900">
                                                    All properties
                                                </label>
                                            </div>
                                        </div>

                                        <template v-for="chosenClient of chosenClients">
                                            <template v-if="chosenClient">
                                                <div class="col-span-6">
                                                    <div class="flex items-center">
                                                        <input @change="handleCheckAllWithin($event, chosenClient)" v-model="checkedClients" name="clients[]" :value="chosenClient.id" :id="'check_all_within' + chosenClient.id" type="checkbox" class="h-4 w-4 text-uknar-secondary focus:ring-uknar-secondary border-gray-300 rounded">
                                                        <label :for="'check_all_within' + chosenClient.id" class="ml-2 block text-sm text-gray-900">
                                                            All properties within "{{ chosenClient.name }}"
                                                        </label>
                                                    </div>

                                                    <div class="ml-5" v-for="property of chosenClient.properties">
                                                        <div class="flex items-center">
                                                            <input :id="'property_' + property.id" v-model="form.properties" :value="property.id" name="properties[]" type="checkbox" class="h-4 w-4 text-uknar-secondary focus:ring-uknar-secondary border-gray-300 rounded">
                                                            <label :for="'property_' + property.id" class="ml-2 block text-sm text-gray-900">
                                                                {{ property.name ?? 'Unnamed property' }}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <div v-for="client of clients">
                                                    <div class="flex items-center">
                                                        <input @change="handleCheckAllWithin($event, client)" v-model="checkedClients" name="clients[]" :value="client.id" :id="'check_all_within' + client.id" type="checkbox" class="h-4 w-4 text-uknar-secondary focus:ring-uknar-secondary border-gray-300 rounded">
                                                        <label :for="'check_all_within' + client.id" class="ml-2 block text-sm text-gray-900">
                                                            All properties within "{{ client.name }}"
                                                        </label>
                                                    </div>

                                                    <div class="ml-5" v-for="property of client.properties">
                                                        <div class="flex items-center">
                                                            <input :id="'property_' + property.id" v-model="form.properties" :value="property.id" name="properties[]" type="checkbox" class="h-4 w-4 text-uknar-secondary focus:ring-uknar-secondary border-gray-300 rounded">
                                                            <label :for="'property_' + property.id" class="ml-2 block text-sm text-gray-900">
                                                                {{ property.name ?? 'Unnamed property' }}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                        </template>
                                    </template>
                                </template>
                            </template>

                            <div class="col-span-6">
                                <div class="flex items-center">
                                    <input v-model="form.can_approve_registers" id="can_approve_registers" name="can_approve_registers" type="checkbox"
                                           class="h-4 w-4 text-uknar-secondary focus:ring-uknar-secondary border-gray-300 rounded">
                                    <label for="can_approve_registers" class="ml-2 block text-sm text-gray-900">
                                        Can accept registers
                                    </label>
                                </div>
                            </div>
                            <div class="col-span-6">
                                <div class="flex items-center">
                                    <input v-model="form.is_active" value="1" id="is_active" name="is_active" type="checkbox"
                                           class="h-4 w-4 text-uknar-secondary focus:ring-uknar-secondary border-gray-300 rounded">
                                    <label for="is_active" class="ml-2 block text-sm text-gray-900">
                                        User Is Active
                                    </label>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <div class="flex justify-end mt-3">
            <a :href="cancelRoute"
               class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-uknar-primary">
                Cancel
            </a>
            <button @click="handleSave"
                    type="button"
                    class="ml-3 inline-flex justify-center py-2 px-4 border border-uknar-primary-dark shadow-sm text-sm font-medium rounded-md text-white bg-uknar-primary hover:bg-uknar-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-uknar-primary">
                Save
            </button>
        </div>

        <session-alert :success="alerts.success" @hide="alerts = { success: null, error:null }"
                       :error="alerts.error" form_error="false"></session-alert>
    </form>
</template>

<script>
import InputField from "../components/form/InputField";
import TextareaField from "../components/form/TextareaField";
import VSelectField from "../components/form/VSelectField";

export default {
    name: "UserForm",

    components: {
        InputField, TextareaField, VSelectField
    },

    props: {
        endpoint: {
            required: true,
            type: String
        },
        csrf: {
            required: true,
            type: String
        },
        portfolios: {
            required: true,
            type: Array,
        },
        clients: {
            required: true,
            type: Array,
        },
        roles: {
            required: true,
            type: Array
        },
        organisationId: {
            required: false,
            type: Number
        },
        portfolioId: {
            required: false,
            type: Number
        },
        isAdmin: {
            required: false,
            type: Boolean
        },
        isPortfolioManager: {
            required: false,
            type: Boolean
        },
        isOrganisationAdmin: {
            required: false,
            type: Boolean
        },
        user: {
            required: false,
            type: Object
        },
        userTypes: {
            required: false,
            type: Array
        },
        isPortfolioManagerFeatureEnabled: {
            required: false,
            type: Boolean
        }
    },

    computed: {
        cancelRoute() {
            return window.route('page.users');
        }
    },

    data() {
        return {
            form: this.emptyForm(),
            chosenClients: [],
            checkedClients: [],
            checkAllProperties: false,
            rolesSelectKey: 1,
            isLoading: true,
            alerts: {
                success: { message: '', },
                error: { message: '' }
            },
            errors: null
        }
    },

    mounted() {
        if (this.portfolioId || this.organisationId) {
            this.form.user_type = 'client';
        }

        if (this.organisationId) {
            this.chosenClients = this.clients.filter(o => o.id == this.organisationId)
        }
    },

    methods: {
        setErrors(error) {
            if (error.response.status === 422) {
                this.errors = error.response.data.errors
            } else {
                this.alerts = {
                    success: null,
                    error: error.response?.data?.message ?? error.message
                }
            }
        },

        displayResponseMessage(response) {
            this.alerts = {
                success:  { message: response.data?.success ? response.data?.message : null },
                error: { message: !response.data?.success ? response.data?.message : null }
            }
        },

        emptyForm() {
            return {
                name: '',
                email: '',
                position: '',
                number: '',
                contact_category: '',
                contact_note: '',
                is_manager: false,
                expiry_date: '',
                client_ids: [],
                portfolio_ids: [],
                role_id: '',
                properties: [],
                is_active: true,
                can_approve_registers: false
            }
        },

        handleSave() {
            if (this.portfolioId) {
                this.form.portfolio_id = this.portfolioId;
                this.form.portfolio_ids = [this.portfolioId];
            }

            if (this.organisationId) {
                this.form.organisation_id = this.organisationId;
                this.form.client_ids = [this.organisationId];
            } else {
                this.form.client_ids = this.chosenClients.map(o => o.id);
            }

            axios.post(this.endpoint, this.form)
                .then(response => {
                    this.displayResponseMessage(response);
                    setTimeout(() => {
                        window.location.href = response.data.redirect;
                    });
                })
                .catch(error => {
                    this.setErrors(error);
                })
        },

        handleCheckAll() {

            let properties = [];
           // console.log(this.chosenClients);
           // console.log(this.clients);
            for (const client of this.chosenClients) {
                properties = properties.concat(client.properties.map(o => o.id));
            }

            if(this.checkAllProperties) {
                this.checkedClients = this.clients.map(o => o.id);
                this.form.properties = properties;
            } else {
                this.checkedClients = [];
                this.form.properties = [];
            }
        },

        handleCheckAllWithin($event, client) {
            if($event.target.checked) {
                this.form.properties = this.form.properties.concat(client.properties.map(o => o.id));
            } else {
                for (const property of client.properties) {
                    const index = this.form.properties.indexOf(property.id);
                    if (index > -1) {
                        this.form.properties.splice(index, 1);
                    }
                }
            }
        },

      handleChosenClientChange(model){
          this.handleCheckAll();
      }
    }
}
</script>

<style scoped>

</style>
